import { type GetUserPlanAvailabilityResponse } from '@/api/gen/_model/getUserPlanAvailabilityResponse';
import { type TrialEndDiscountInfo } from '@/api/gen/_model/trialEndDiscountInfo';

export type { TrialEndDiscountInfo };

/** 견적/주문 응답의 적용 프로모션 종류(생성 타입은 `string`이라 여기서 좁힌다). */
export type AppliedPromotionType =
  | 'BASIC_FIRST_PURCHASE'
  | 'TRIAL_END_DISCOUNT';

/**
 * 견적/정산 응답에서 적용된 프로모션 종류를 읽는다.
 *
 * `POST /quiz-plans/payment/estimate`(PriceCalculationResponse)에는 아직 이 필드가 없어
 * 신규 구매 체크아웃에서는 항상 null 이다 — 서버가 추가하면 자동으로 붙는다.
 */
export function readAppliedPromotionType(
  response: object | undefined,
): AppliedPromotionType | null {
  const withPromotion = response as
    | { appliedPromotionType?: string | null }
    | undefined;
  const value = withPromotion?.appliedPromotionType;
  return value === 'BASIC_FIRST_PURCHASE' || value === 'TRIAL_END_DISCOUNT'
    ? value
    : null;
}

export type TrialEndDiscountStatus = 'none' | 'active' | 'urgent' | 'ended';

/** 24시간 미만이면 배너가 빨강으로 바뀐다(Figma 29139:3340). */
export const TRIAL_END_DISCOUNT_URGENT_MS = 24 * 60 * 60 * 1000;

export interface TrialEndDiscountState {
  status: TrialEndDiscountStatus;
  trialEndAt: Date | null;
  windowEnd: Date | null;
  discountRate: number;
  remainingMs: number;
  maxMonthCount: number;
  maxUserCount: number;
  displayDiscountedMonthlyPrice: number | null;
  displayOriginalMonthlyPrice: number | null;
  displayCurrency: string | null;
}

export const NO_TRIAL_END_DISCOUNT: TrialEndDiscountState = {
  status: 'none',
  trialEndAt: null,
  windowEnd: null,
  discountRate: 0,
  remainingMs: 0,
  maxMonthCount: 0,
  maxUserCount: 0,
  displayDiscountedMonthlyPrice: null,
  displayOriginalMonthlyPrice: null,
  displayCurrency: null,
};

/**
 * 체험 종료 후 7일 20% 할인 (PRD 솔루션 6).
 *
 * 체험 이력이 없거나 이미 할인 구매를 마쳤으면 서버가 내려주지 않는다 →
 * 아무것도 그리지 않는다.
 */
export function readTrialEndDiscount(
  availability: GetUserPlanAvailabilityResponse | undefined,
): TrialEndDiscountInfo | null {
  return availability?.trialEndDiscount ?? null;
}

export function resolveTrialEndDiscountState(
  info: TrialEndDiscountInfo | null,
  nowMs: number,
): TrialEndDiscountState {
  if (!info) return NO_TRIAL_END_DISCOUNT;

  const trialEndAtMs = Date.parse(info.trialEndAt);
  const windowEndMs = Date.parse(info.windowEnd);
  if (Number.isNaN(trialEndAtMs) || Number.isNaN(windowEndMs)) {
    return NO_TRIAL_END_DISCOUNT;
  }

  const remainingMs = Math.max(0, windowEndMs - nowMs);
  const base = {
    trialEndAt: new Date(trialEndAtMs),
    windowEnd: new Date(windowEndMs),
    discountRate: info.discountRate,
    remainingMs,
    maxMonthCount: info.maxMonthCount,
    maxUserCount: info.maxUserCount,
    displayDiscountedMonthlyPrice: info.displayDiscountedMonthlyPrice,
    displayOriginalMonthlyPrice: info.displayOriginalMonthlyPrice,
    displayCurrency: info.displayCurrency,
  };

  if (nowMs < trialEndAtMs) return NO_TRIAL_END_DISCOUNT;
  // 기한이 지났으면 자격 여부와 무관하게 "할인 종료" 안내를 한 번 보여준다.
  if (remainingMs <= 0) return { ...base, status: 'ended' };
  // 기한은 남았는데 자격이 없다 = 이미 할인 구매를 썼다 → 노출 안 함.
  if (!info.eligible) return NO_TRIAL_END_DISCOUNT;

  return {
    ...base,
    status: remainingMs < TRIAL_END_DISCOUNT_URGENT_MS ? 'urgent' : 'active',
  };
}

/** 남은 시간을 일/시/분으로 쪼갠다. 초는 쓰지 않는다(1분 단위 갱신). */
export function splitRemaining(remainingMs: number) {
  const totalMinutes = Math.floor(Math.max(0, remainingMs) / 60_000);
  return {
    days: Math.floor(totalMinutes / (24 * 60)),
    hours: Math.floor((totalMinutes % (24 * 60)) / 60),
    minutes: totalMinutes % 60,
  };
}
